import { config } from "config/config";
import { isYeloAppSession } from "utils/partnerSession.util";
import { getStoredPartnerUser } from "utils/partnerUser.util";

const LOCAL_ENVS = new Set(["development", "dev", "test", "testing"]);

function normalizedEnv(): string {
  return (config.APP_ENV || config.NODE_ENV || "").toLowerCase();
}

/**
 * Yelo app webview only.
 * Local and staging are off. Staging uses the same token as production today,
 * so it must not send until it has its own project.
 */
export function shouldEnableYeloAppAnalytics(): boolean {
  if (process.env.REACT_APP_DISABLE_ANALYTICS === "true") return false;
  if (!config.MIXPANEL_TOKEN) return false;

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return false;
  }

  const env = normalizedEnv();
  if (LOCAL_ENVS.has(env) || env === "staging" || env === "qa") return false;

  return isYeloAppSession();
}

export function analyticsEnvironment(): string {
  const env = normalizedEnv();
  if (env === "production" || env === "prod") return "production";
  if (env === "staging" || env === "qa") return "staging";
  return env || "production";
}

export function analyticsLocale(): "en" | "ar" {
  if (typeof window === "undefined") return "en";
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  if (segment === "ar" || segment === "en") return segment;
  const stored = window.localStorage.getItem("lang");
  return stored === "ar" ? "ar" : "en";
}

/** Present only when the Yelo app puts it on the page address. */
export function readAppVersion(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return (params.get("app_version") || params.get("appVersion") || "").trim();
}

/**
 * Internal Yelo Plus account id. Email, phone, and the Yelo customer
 * reference are never used as the Mixpanel id.
 */
export function yeloAppAnalyticsUserId(): string {
  const user = getStoredPartnerUser() as Record<string, unknown> | null;
  if (!user) return "";
  const raw = user.mongo_user_id || user._id;
  let id = "";
  if (typeof raw === "string") id = raw.trim();
  else if (raw && typeof raw === "object") {
    const record = raw as { $oid?: string; toString?: () => string };
    id = record.$oid || (typeof record.toString === "function" ? record.toString() : "");
  }
  if (!id || id === "[object Object]" || id.includes("@")) return "";
  if (/^\+?\d{8,15}$/.test(id)) return "";
  return id;
}

/** @deprecated Use shouldEnableYeloAppAnalytics */
export const shouldEnablePartnerAnalytics = shouldEnableYeloAppAnalytics;
